import { Music, Pause, Play } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState, type FC } from 'react'
import useSound from 'use-sound'
import type { AudioPayload } from './_types'
import { useLocale } from 'next-intl'
import { Spin } from '@/components/ui/spin'
import { cn } from '@/lib/utils'
import { useAudioController } from './context'

// 将时间转成 分:秒 格式
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60 / 1000)
  const secs = Math.floor((seconds / 1000) % 60)
  return `${String(mins)}:${String(secs).padStart(2, '0')}`
}

export interface PlayProps {
  item: AudioPayload
  disabled?: boolean
}

export type SoundState = 'idle' | 'playing' | 'paused' | 'end'

export const Audio: FC<PlayProps> = (props) => {
  const {
    item: { url, title, titles },
    disabled = false,
  } = props
  const locale = useLocale()
  const { setCurrentSound, getCurrentSound, stopCurrentSound } =
    useAudioController()

  const [loaded, setLoaded] = useState<boolean>(false)

  const [play, { sound, stop, pause, duration }] = useSound(url)

  const localeTitle = useMemo(() => {
    if (locale === 'zh') return titles['zh-Hans']
    return titles[locale]
  }, [locale, titles])

  const [soundState, setSoundState] = useState<SoundState>('idle')

  const playAudio = useCallback(() => {
    const currentSound = getCurrentSound()
    if (currentSound) {
      if (currentSound === sound) return
      stopCurrentSound()
    }
    if (sound) {
      play()
      setSoundState('playing')
      setCurrentSound(sound, {
        onStop: () => {
          setSoundState('idle')
        },
      })
    }
  }, [sound, play, getCurrentSound, stopCurrentSound, setCurrentSound])

  const pauseAudio = useCallback(() => {
    stopCurrentSound()
    pause()
    setSoundState('paused')
  }, [pause, stopCurrentSound])

  const loadAudio = useCallback(() => {
    if (disabled) return
    if (!sound || sound?.state() === 'loading') return
    if (sound?.state() === 'loaded' && loaded === false) {
      setLoaded(true)
      return
    }
    sound.load()
  }, [sound, loaded, disabled])

  useEffect(() => {
    if (!sound) return

    const onEnd = () => {
      setSoundState('end')
      stopCurrentSound()
    }

    sound.on('end', onEnd)

    // 清理事件监听器
    return () => {
      sound.off('end', onEnd)
    }
  }, [sound, stopCurrentSound])

  useEffect(() => {
    loadAudio()
  }, [loadAudio])

  return (
    <div className="relative flex items-center h-full rounded-xl bg-gray-800 px-4 py-2 overflow-hidden hover:bg-gray-700 transition-all duration-200 group">
      <Spin spinning={loaded === false && !disabled}>
        <div className="flex gap-3 min-w-0">
          <div className="play w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 relative bg-gray-700 group-hover:bg-blue-500">
            {['idle', 'paused', 'end'].includes(soundState) && (
              <>
                {!disabled && (
                  <div className="z-10 transition-opacity duration-200 group-hover:opacity-100 opacity-0">
                    <Play size={16} onClick={playAudio} />
                  </div>
                )}

                <Music
                  className={cn('absolute text-blue-400 opacity-100', {
                    'transition-opacity duration-200 group-hover:opacity-0':
                      !disabled,
                  })}
                />
              </>
            )}
            {/* 旋转边框圆圈 */}
            {soundState === 'playing' && (
              <>
                <div className="absolute inset-0 rounded-full border-2 border-[#93c5fd] border-t-transparent animate-spin" />
                <div className="absolute inset-[2px] flex items-center justify-center bg-blue-600 rounded-full text-white">
                  <Pause className="w-5 h-5" onClick={pauseAudio} />
                </div>
              </>
            )}
          </div>
          <div className="flex flex-col flex-1 min-w-0">
            <div className="truncate text-small">{localeTitle}</div>
            {duration && (
              <div className="text-white/60 text-small">
                {formatDuration(duration)}
              </div>
            )}
          </div>
        </div>
      </Spin>
    </div>
  )
}
